import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import { Stack } from '@mui/material';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import {
  Button,
  Button as LuminaryButton,
  ButtonProps,
} from '@/components/form/baseInputs/Button';
import { Bold01Icon } from '@/components/icons/Bold01Icon';
import { Italic01Icon } from '@/components/icons/Italic01Icon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Strikethrough01Icon } from '@/components/icons/Strikethrough01Icon';
import { Underline01Icon } from '@/components/icons/Underline01Icon';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { COLORS } from '@/styles/tokens/colors';

import { TextEditorKind } from '../../TextEditor';
import { BlockOptionsDropdown } from './BlockOptionsDropdown';
import { HighlightButton } from './HighlightButton';
import { TableActionToolbar } from './TableActionToolbar';
import { TableInsertButton } from './TableInsertButton';
import { isSelectionInTable } from './tableUtils';
import { Block, iconProps } from './ToolbarPlugin.utils';

export const TextEditorToolbarButton = ({
  children,
  isActive,
  sx,
  ...props
}: PropsWithChildren<Partial<ButtonProps> & { isActive: boolean }>) => {
  // Merge the base styles with any caller-provided sx so a consumer passing
  // their own sx doesn't wipe out padding / default bgcolor.
  const userSx = Array.isArray(sx) ? sx : sx ? [sx] : [];
  return (
    <LuminaryButton
      size="sm"
      variant="secondary"
      sx={[
        {
          padding: '6px',
          bgcolor: isActive ? COLORS.GRAY[300] : COLORS.PRIMITIVES.WHITE,
        },
        ...userSx,
      ]}
      {...props}
    >
      {children}
    </LuminaryButton>
  );
};

const LowPriority = 1;

interface ToolbarPluginProps {
  textEditorKind: TextEditorKind;
  excludeBackground?: boolean;
  openGenerateAIEntityNotesModal?: () => void;
  generateAIButtonDisabled?: boolean;
  omitBlockTypes?: Block[];
  allowTableInsert?: boolean;
  showCompactBlockDropdown?: boolean;
}

export function ToolbarPlugin({
  textEditorKind,
  excludeBackground: isChildToolbar,
  openGenerateAIEntityNotesModal,
  generateAIButtonDisabled,
  omitBlockTypes,
  allowTableInsert = true,
  showCompactBlockDropdown,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<Block>('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isInTable, setIsInTable] = useState(false);
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();

  const isMarkdown = textEditorKind === 'markdown';

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? (element.getTag() as Block)
            : (element.getType() as Block);
          setBlockType(type);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsUnderline(selection.hasFormat('underline'));
    }
    // Update table state
    setIsInTable(isSelectionInTable());
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setSelection(selection.clone());
          }
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  return (
    <Stack
      p={1}
      bgcolor={isChildToolbar ? undefined : COLORS.GRAY[200]}
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={
        isChildToolbar
          ? undefined
          : {
              borderRadius: 1,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
      }
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <BlockOptionsDropdown
          editor={editor}
          blockType={blockType}
          omitBlockTypes={omitBlockTypes}
          compact={showCompactBlockDropdown}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {allowTableInsert && (
          <TableInsertButton
            editor={editor}
            isInTable={isInTable}
            // Match the compact 6px padding used by adjacent
            // TextEditorToolbarButtons in this legacy toolbar so the table
            // button doesn't read as oversized next to its siblings.
            buttonSx={{ padding: '6px' }}
          />
        )}
        <TextEditorToolbarButton
          isActive={isBold}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
        >
          <Bold01Icon {...iconProps} />
        </TextEditorToolbarButton>
        <TextEditorToolbarButton
          isActive={isItalic}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
        >
          <Italic01Icon {...iconProps} />
        </TextEditorToolbarButton>
        {!isMarkdown && (
          <TextEditorToolbarButton
            isActive={isUnderline}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
          >
            <Underline01Icon {...iconProps} />
          </TextEditorToolbarButton>
        )}
        <TextEditorToolbarButton
          isActive={isStrikethrough}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          }}
        >
          <Strikethrough01Icon {...iconProps} />
        </TextEditorToolbarButton>
        <HighlightButton />
        <TableActionToolbar />
        {openGenerateAIEntityNotesModal != undefined &&
          aiCapabilitiesEnabled && (
            <Button
              size="sm"
              variant="secondary"
              startIcon={Stars01Icon}
              onClick={openGenerateAIEntityNotesModal}
              disabled={generateAIButtonDisabled}
            >
              Generate with AI
            </Button>
          )}
      </Stack>
    </Stack>
  );
}
